import React, { useContext, useEffect, useState, } from 'react';
import { bool, shape, string, number } from 'prop-types';
import classNames from 'classnames';
import {
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  bool as boolType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { IntroOffer } from './intro-offer/legacy-intro-offer.component';
import { PaymentEstimator } from './payment-estimator/legacy-payment-estimator.component';
import { OffersForYou } from './offers-for-you/legacy-offers-for-you.component';
import { loadEstimatorScript } from '../utils/payment-estimator-utils';
import './consumer-card-messaging.style.scss';

const isValidItemId = (itemId) => /^\d{9}$/.test(itemId);
const isValidBundlePrice = (bundlePrice) => {
  return Boolean(
    bundlePrice?.length
  );
};
const isValidProps = (props) => {
  const { itemId, bundlePrice } = props;
  return isValidItemId(itemId) || isValidBundlePrice(bundlePrice);
};
const isValidData = (data) => {
  return !!(data?.product?.pricing);
};
const isValidProduct = (product) => !!product?.pricing && !!product?.identifiers;
export const ConsumerCardMessaging = (props) => {
  const theme = useTheme(ConsumerCardMessaging, props);
  const { showHDCC } = theme.props;

  const { channel } = useContext(ExperienceContext);
  const { isLocalized } = useStore();
  const paymentEstimatorVersion = useConfigService('PaymentEstimatorVersion') || 'v1.7.1';
  const generalThreshold = parseFloat(useConfigService('PipPaymentEstimatorThreshold')) || 850.00;
  const applianceThreshold = parseFloat(useConfigService('AppliancePipPaymentEstimatorThreshold')) || 300.00;

  const [paymentEstimatorLoaded, setPaymentEstimatorLoaded] = useState(false);

  const {
    itemId,
    product,
    bundlePrice,
    isAppliance,
    isFinancePromoEligible,
    clsRemediation,
    isB2B,
    showEverydayFinancing
  } = props;
  const { configuredPrice } = useLifeCycleEventBus('configurator.configurator_pricing_event');
  const hasValidBundlePrice = isValidBundlePrice(bundlePrice);
  const hasValidProduct = isValidProduct(product);
  const hasValidProps = isValidProps(props) || showEverydayFinancing || hasValidBundlePrice || hasValidProduct;
  const { data, loading } = useDataModel('product', {
    variables: { itemId },
    skip: !hasValidProps || hasValidBundlePrice || hasValidProduct
  });

  useEffect(() => {
    loadEstimatorScript(paymentEstimatorVersion, () => setPaymentEstimatorLoaded(true), channel === 'mobile');
  }, []);

  const hasValidData = showEverydayFinancing || isValidData(data) || hasValidBundlePrice || hasValidProduct;
  const hasDataFullyLoaded = paymentEstimatorLoaded && isLocalized && !loading;

  if (!hasValidProps) {
    return null;
  }

  if (!hasValidData) {
    const { placeholders, preservePlaceholders } = clsRemediation;
    if (!placeholders) {
      return null;
    }
    if (hasDataFullyLoaded && !preservePlaceholders) {
      return null;
    }
  }
  const consumerCardClasses = classNames({
    'consumer-card-messaging--hidden': !hasValidData || !hasDataFullyLoaded
  });
  const productData = hasValidProduct ? product : data?.product;
  const isProductAppliance = productData?.identifiers?.productType === 'MAJOR_APPLIANCE';
  const pipPaymentEstimatorThreshold = (isAppliance || isProductAppliance) ? applianceThreshold : generalThreshold;
  const value = configuredPrice?.pricing?.value || productData?.pricing?.value;
  const unitOfMeasure = configuredPrice?.pricing?.unitOfMeasure || productData?.pricing?.unitOfMeasure;
  const alternatePriceDisplay = configuredPrice?.pricing?.alternatePriceDisplay
    || productData?.pricing?.alternatePriceDisplay;
  const alternateUnitOfMeasure = alternatePriceDisplay && unitOfMeasure.toLowerCase() !== 'each';
  const showPaymentEstimator = showEverydayFinancing
    || (!isB2B && !hasValidBundlePrice && (value >= pipPaymentEstimatorThreshold || value === null));
  const showIntroOfferB2C = !hasValidBundlePrice && !showPaymentEstimator && value >= 25.0
    && value < pipPaymentEstimatorThreshold && !isB2B;
  const showIntroOfferB2B = !hasValidBundlePrice && !showPaymentEstimator && (value >= 25.0 || value === null) && isB2B;
  const showConsumerCard = (hasValidBundlePrice || showPaymentEstimator || (showIntroOfferB2C || showIntroOfferB2B))
    && showHDCC;
  if (!showConsumerCard) {
    return <></>;
  }
  return (
    <div className={consumerCardClasses} data-component="ConsumerCardMessaging">
      {hasValidBundlePrice && (
        <OffersForYou
          price={bundlePrice}
          isMobile={channel === 'mobile'}
          isAppliance={isAppliance}
          paymentEstimatorLoaded={paymentEstimatorLoaded}
        />
      )}
      {showPaymentEstimator && (
        <PaymentEstimator
          price={value}
          isMobile={channel === 'mobile'}
          isAppliance={isProductAppliance}
          isHDHome={isFinancePromoEligible}
          paymentEstimatorLoaded={paymentEstimatorLoaded}
          showEverydayFinancing={showEverydayFinancing || value === null}
          isCountertops={showEverydayFinancing}
        />
      )}
      {(showIntroOfferB2C || showIntroOfferB2B) && (
        <IntroOffer
          price={value}
          isB2B={isB2B}
          mobile={channel === 'mobile'}
          showGenericDiscount={alternateUnitOfMeasure || value === null}
          paymentEstimatorLoaded={paymentEstimatorLoaded}
        />
      )}
    </div>
  );
};
ConsumerCardMessaging.displayName = 'ConsumerCardMessaging';
ConsumerCardMessaging.dataModel = {
  product: params({
    itemId: stringType().isRequired()
  }).shape({
    dataSources: stringType(),
    pricing: params({ storeId: stringType(), isBrandPricingPolicyCompliant: boolType() }).shape({
      value: numberType(),
      unitOfMeasure: stringType(),
      alternatePriceDisplay: boolType()
    }),
    identifiers: shapeType({
      productType: stringType()
    })
  })
};
const checkRequiredProps = (props) => {
  if (props.showEverydayFinancing) {
    return null;
  }
  if (!props.itemId && !props.bundlePrice && !props.product) {
    return new Error(
      'The \'itemId\', \'bundlePrice\' or \'product\' is required by the consumer-card-messaging component.'
    );
  }
  return null;
};

ConsumerCardMessaging.propTypes = {
  itemId: checkRequiredProps,
  product: checkRequiredProps,
  bundlePrice: checkRequiredProps,
  isAppliance: bool,
  isFinancePromoEligible: bool,
  clsRemediation: shape({
    placeholders: bool,
    preservePlaceholders: bool
  }),
  isB2B: bool,
  showEverydayFinancing: bool
};

ConsumerCardMessaging.defaultProps = {
  itemId: null,
  product: null,
  bundlePrice: null,
  isAppliance: false,
  isFinancePromoEligible: false,
  clsRemediation: {
    placeholders: false,
    preservePlaceholders: false
  },
  isB2B: false,
  showEverydayFinancing: false
};

ConsumerCardMessaging.themeProps = {
  showHDCC: bool
};

ConsumerCardMessaging.defaultThemeProps = {
  showHDCC: true
};
